<template>
  <div class="material-input-card">
    <RecycoModal name="modal">{{ modalText }}</RecycoModal>
    <div class="header">
      <span class="title">{{ $t('load-products') }}</span>
      <FontAwesomeIcon icon="times" @click="close" />
    </div>
    <form class="form" @submit.prevent="submit">
      <RecycoSelect :label="$t('station')" :disabled="!stations" :options="stations || []" name-key="name" v-model="station" :validate="(s) => !s ? $t('errors.station-must-not-be-empty') : undefined">
        <template #search="{ attributes, events }">
          <input
            class="vs__search"
            required
            v-bind="attributes"
            v-on="events"
          />
        </template>
      </RecycoSelect>
      <RecycoSelect :label="$t('product')" :disabled="!station" :options="station ? station.organization.products : []" name-key="name" v-model="product" :validate="(p) => !p ? $t('errors.product-must-not-be-empty') : undefined">
        <template #search="{ attributes, events }">
          <input
            class="vs__search"
            required
            v-bind="attributes"
            v-on="events"
          />
        </template>
      </RecycoSelect>
      <RecycoInput :label="$t('quantity')" type="number" v-model="quantity" min="1" pattern="\d*" step="1" :validate="(n) => n < 1 ? $t('errors.quantity-positive-integer') : undefined" />
      <RecycoButton type="submit">{{ $t(loading ? 'loading' : 'submit') }}</RecycoButton>
    </form>
    <div v-if="estimatedRecycoins">{{ $t('estimated-recycoins') }}: {{ estimatedRecycoins }}</div>
  </div>
</template>

<i18n>
  {
    "es": {
      "load-products": "Cargar Productos",
      "station": "Puesto",
      "product": "Producto",
      "quantity": "Cantidad",
      "loading": "Cargando...",
      "submit": "Cargar",
      "estimated-recycoins": "Recycoins estimadadas",
      "errors": {
        "nothing-empty": "Ninguno de los campos pueden quedar vacíos",
        "station-must-not-be-empty": "El campo de puesto no puede dejarse vacio",
        "product-must-not-be-empty": "El campo de producto no puede dejarse vacio",
        "quantity-positive-integer": "La cantidad debe ser un número entero positivo",
        "material-upload": "Se produjo un error grabando la transacción, por favor intente nuevamente"
      },
      "material-uploaded-alert": "Se han subido {quantity} unidade(s) de {product}. Se cargaron {coins} monedas a la cuenta de {name}."
    },
    "en": {
      "load-products": "Load Products",
      "station": "Station",
      "product": "Product",
      "quantity": "Quantity",
      "loading": "Loading...",
      "submit": "Submit",
      "estimated-recycoins": "Estimated Recycoins",
      "errors": {
        "nothing-empty": "None of the fields can be left empty can be empty",
        "station-must-not-be-empty": "The station field cannot be left empty",
        "product-must-not-be-empty": "The product field cannot be left empty",
        "quantity-positive-integer": "Quantity must be a positive integer",
        "material-upload": "An error ocurred while saving the transaction, please try again"
      },
      "material-uploaded-alert": "{quantity} unit(s) of {product} have been uploaded. {coins} were added to {name}'s account."
    }
  }
</i18n>

<script>
  import gql from 'graphql-tag';
  import { getInstance } from '@/plugins/auth0/use-auth0';

  import {
    RecycoSelect,
    RecycoInput,
    RecycoButton,
    RecycoModal,
  } from '@/components/recyco';

  export default {
    name: 'material-input-card',
    props: {
      userId: {
        type: String,
        required: true,
      },
    },
    components: {
      RecycoSelect,
      RecycoInput,
      RecycoButton,
      RecycoModal,
    },
    data() {
      return {
        modalText: undefined,
        station: undefined,
        product: undefined,
        organizationsWithOperatorRole: undefined,
        quantity: 1,
        loading: false,
      };
    },
    apollo: {
      organizations: {
        query: gql`query me {
          me {
            organizations {
              uuid
              products {
                uuid
                name
                material
                value
              }
              stations {
                uuid
                name
              }
            }
          }
        }`,
        update: ({ me }) => me.organizations,
        fetchPolicy: 'cache-and-network',
      },
    },
    methods: {
      showModal(text) {
        this.modalText = text;
        this.$modal.show('modal');
      },
      close() {
        this.$emit('close');
      },
      resetForm() {
        this.product = undefined;
        this.quantity = 1;
      },
      async submit() {
        const {
          userId,
          quantity,
          product,
          station,
        } = this;
        if (!quantity || !station || !product) {
          this.showModal(this.$t('errors.nothing-empty'));
          return;
        }
        try {
          this.loading = true;
          const { data: { createTransaction: res } } = await this.$apollo.mutate({
            mutation: gql`mutation createTransaction($input: CreateTransactionInput!) {
              createTransaction(input: $input) {
                uuid
                items {
                  quantity
                  product {
                    name
                  }
                  reward
                }
                user {
                  idpUserData {
                    name
                  }
                }
                createdAt
              }
            }`,
            variables: {
              input: {
                userId,
                items: [{
                  quantity: parseInt(quantity, 10),
                  productUuid: product.uuid,
                }],
                stationUuid: station.uuid,
              },
            },
          });
          console.log(res);
          this.showModal(this.$t('material-uploaded-alert', {
            quantity: res.items[0].quantity,
            product: res.items[0].product.name,
            coins: res.items[0].reward,
            name: res.user.idpUserData.name,
          }));
          this.resetForm();
          this.loading = false;
        } catch (err) {
          console.error(err);
          // eslint-disable-next-line no-alert
          this.showModal(this.$t('errors.material-upload'));
          this.resetForm();
        }
      },
    },
    computed: {
      estimatedRecycoins() {
        return this.product ? this.product.value * this.quantity : undefined;
      },
      stations() {
        if (!this.organizationsWithOperatorRole || !this.organizations) return undefined;
        const organizations = this.organizations.filter(({ uuid }) => this.organizationsWithOperatorRole.includes(uuid));
        const ss = organizations.map(({ products, stations }) => stations.map((s) => {
          const station = s;
          station.organization = stations.organization ? { ...station.organization, products } : { products };
          return station;
        })).flat();
        console.log(ss);
        return ss;
      },
    },
    async mounted() {
      const authService = getInstance();
      const { organizations } = await authService.getUserRoles();
      this.organizationsWithOperatorRole = organizations.filter(({ roles }) => roles.operator).map(({ id }) => id);
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/styles/mixins.scss';

  .material-input-card {
    background-color: white;
    border-radius: 2em;

    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: stretch;
    padding: 2em;
    @include separate-children('vertical', 1rem);
  }

  .header {
    font-size: 2em;
    font-weight: bold;

    display: flex;
    justify-content: space-between;
  }
</style>
